// 🎯 Hook personalizado para generación de facturas internas
// Maneja el estado y lógica de generación individual y en lote
import { useState, useCallback } from "react";
import { useSnackbar } from "notistack";
import {
  generateInternalInvoice,
  generateInternalInvoicesBatch
} from "@/services/internal-invoices-api";
import type {
  GenerateInternalInvoiceRequest,
  BatchGenerateInternalInvoiceRequest,
  InternalInvoiceGenerationResultDto,
  BatchGenerateInternalInvoiceResponse,
  InternalInvoiceGenerationState,
  BatchGenerationState
} from "@/types/internal-invoices";
export interface UseInternalInvoiceGenerationReturn {
  // Estado para generación individual
  singleGeneration: InternalInvoiceGenerationState;
  // Estado para generación en lote
  batchGeneration: BatchGenerationState;
  // Acciones
  generateSingle: (request: number | GenerateInternalInvoiceRequest) => Promise<void>;
  generateBatch: (carrierInvoiceIds: number[]) => Promise<void>;
  resetSingleState: () => void;
  resetBatchState: () => void;
}
export function useInternalInvoiceGeneration(): UseInternalInvoiceGenerationReturn {
  const { enqueueSnackbar } = useSnackbar();
  // Estado para generación individual
  const [singleGeneration, setSingleGeneration] = useState<InternalInvoiceGenerationState>({
    isGenerating: false,
    result: null,
    error: null,
  });
  // Estado para generación en lote
  const [batchGeneration, setBatchGeneration] = useState<BatchGenerationState>({
    isGenerating: false,
    result: null,
    error: null,
  });
  /**
   * Genera una factura interna individual
   */
  const generateSingle = useCallback(async (requestOrCarrierInvoiceId: number | GenerateInternalInvoiceRequest) => {
    setSingleGeneration({
      isGenerating: true,
      result: null,
      error: null,
    });
    try {
      const request: GenerateInternalInvoiceRequest =
        typeof requestOrCarrierInvoiceId === "number"
          ? { carrierInvoiceId: requestOrCarrierInvoiceId, rateMode: "Automatic" }
          : requestOrCarrierInvoiceId;
      const result = await generateInternalInvoice(request);
      setSingleGeneration({
        isGenerating: false,
        result,
        error: null,
      });
      // Mostrar notificación según el resultado
      if (result.success) {
        enqueueSnackbar(
          `Factura interna generada exitosamente (ID: ${result.internalInvoiceId})`,
          { variant: "success" }
        );
        // Mostrar warnings si existen
        if (result.warnings?.length > 0) {
          enqueueSnackbar(
            `${result.warnings.length} advertencia(s) en la generación`,
            { variant: "warning" }
          );
        }
      } else {
        enqueueSnackbar(
          `Error generando factura interna: ${result.error}`,
          { variant: "error" }
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error desconocido";
      setSingleGeneration({
        isGenerating: false,
        result: null,
        error: errorMessage,
      });
      enqueueSnackbar(
        `Error generando factura interna: ${errorMessage}`,
        { variant: "error" }
      );
    }
  }, [enqueueSnackbar]);
  /**
   * Genera facturas internas en lote
   */
  const generateBatch = useCallback(async (carrierInvoiceIds: number[]) => {
    setBatchGeneration({
      isGenerating: true,
      result: null,
      error: null,
    });
    try {
      const request: BatchGenerateInternalInvoiceRequest = {
        carrierInvoiceIds,
      };
      const result = await generateInternalInvoicesBatch(request);
      setBatchGeneration({
        isGenerating: false,
        result,
        error: null,
      });
      // Mostrar resumen del lote
      const { totalSuccess, totalFailed, totalRequested } = result;
      if (totalSuccess > 0) {
        enqueueSnackbar(
          `Lote procesado: ${totalSuccess}/${totalRequested} facturas generadas exitosamente`,
          { variant: "success" }
        );
      }
      if (totalFailed > 0) {
        // Detectar si algún fallo es por booking no conciliado
        const failedResults = result.results.filter(r => !r.success);
        const pendingMatchErrors = failedResults.filter(r =>
          r.error?.toLowerCase().includes("concilia") ||
          r.error?.toLowerCase().includes("pending") ||
          r.error?.toLowerCase().includes("repo_doc") ||
          r.error?.toLowerCase().includes("vinculad")
        );

        if (pendingMatchErrors.length > 0) {
          enqueueSnackbar(
            `${pendingMatchErrors.length} factura(s) bloqueadas: el booking asociado no está conciliado`,
            { variant: "warning" }
          );
          const otherErrors = totalFailed - pendingMatchErrors.length;
          if (otherErrors > 0) {
            enqueueSnackbar(
              `${otherErrors} factura(s) fallaron por otros motivos`,
              { variant: "error" }
            );
          }
        } else {
          enqueueSnackbar(
            `${totalFailed} factura(s) no pudieron generarse`,
            { variant: "warning" }
          );
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Error desconocido";
      setBatchGeneration({
        isGenerating: false,
        result: null,
        error: errorMessage,
      });
      enqueueSnackbar(
        `Error en generación por lote: ${errorMessage}`,
        { variant: "error" }
      );
    }
  }, [enqueueSnackbar]);
  /**
   * Resetea el estado de generación individual
   */
  const resetSingleState = useCallback(() => {
    setSingleGeneration({
      isGenerating: false,
      result: null,
      error: null,
    });
  }, []);
  /**
   * Resetea el estado de generación por lote
   */
  const resetBatchState = useCallback(() => {
    setBatchGeneration({
      isGenerating: false,
      result: null,
      error: null,
    });
  }, []);
  return {
    singleGeneration,
    batchGeneration,
    generateSingle,
    generateBatch,
    resetSingleState,
    resetBatchState,
  };
}










